import { pluralize, triggerNudgeAlert } from "./otherUtils.jsx";
import {
  buildTaskEntityColorMenu,
  buildTaskEntityStyleMenu, capitalizeText,
  openAgendaView,
  openNewSpotIncentiveEditor,
  showUnlinkRelatedEntityPrompt
} from "./utils.jsx";
import { MessageDialog } from "@bryntum/schedulerpro";
import { STORE_KEYS } from "../constants/StoreKeys.jsx";
import { TAGS_KEYS } from "../constants/TagsKeys.jsx";
import {WIDGET_KEYS} from "../constants/WidgetKeys.jsx";
import { EDITOR_KEYS } from "../constants/EditorKeys.jsx";
import { ALERT_TYPES } from "../constants/OtherKeys.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "../constants/IncentiveKeys.jsx";
import { checkIncentivePermission } from "./incentiveUtils.jsx";

export const entityStatusSelector = (params = {}) => ({
  text: "Status",
  icon: "fa-light fa-bars-progress",
  weight: 1,
  menu: [],
  disabled: true,
  ...params
});

export const entityCategorySelector = (params = {}) => ({
  text: "Category",
  icon: "fa-light fa-symbols",
  weight: 2,
  menu: [],
  disabled: true,
  ...params
});

export const entityPhaseSelector = (params = {}) => ({
  text: "Phase",
  icon: "fa-light fa-arrow-progress",
  weight: 3,
  menu: [],
  disabled: true,
  ...params
});

export const entityTagsSelector = (params = {}) => ({
  text: "Tags",
  icon: "fa-light fa-tags",
  weight: 4,
  menu: [],
  disabled: true,
  ...params
});

export const entityTeamSelector = (params = {}) => ({
  text: "Team",
  icon: "fa-light fa-people-group",
  weight: 5,
  menu: [],
  disabled: true,
  ...params
});

export const entitySendBoardAlert = (params = {}) => ({
  text: "Board Alert",
  icon: "fa-light fa-send",
  weight: 8,
  ...params
});

export const cellMenuItemEditEntity = (params = {}) => ({
  text: "Edit",
  icon: "fa fa-edit",
  weight: 50,
  ...params
});

export const cellMenuItemViewAgenda = (params = {}) => ({
  text: "View Agenda",
  icon: "fa fa-bars",
  weight: 100,
  ...params
});

export const cellMenuItemRemoveRow = (params = {}) => ({
  weight: 400,
  text: "Delete",
  icon: "fa fa-trash",
  ...params,
});

export const cellMenuItemToggleArchiveRow = (params = {}) => ({
  weight: 1000,
  icon: "fa-solid fa-folder-open",
  onItem: ({ record }) => {
    if (!record.archived && record.swivuUnlinkPromptConfig) showUnlinkRelatedEntityPrompt(record.swivuUnlinkPromptConfig);
    else record.archived = !record.archived;
  },
  ...params,
  cls: 'border-0 !border-t !border-solid dark:!border-[#131315] !border-[#cccccc]'
});

export const cellMenuItemCollapseRow = (params = {}) => ({
  weight: 500,
  hidden: true,
  ...params,
});

export const cellMenuItemCollapseAllRows = (params = {}) => ({
  weight: 600,
  hidden: true,
  ...params,
});

export const cellMenuItemSettingsEditor = (params = {}) => ({
  text: "Settings",
  weight: 700,
  icon: "fa-solid fa-gear",
  hidden: true,
  ...params,
});

export const cellMenuItemCopyContactToClipboard = (params = {}) => ({
  text: "Copy To Clipboard",
  weight: 800,
  icon: "fa-solid fa-copy",
  ...params,
});

export const entityCellTaskColor = (params = {}) => ({
  text: "Task Color",
  icon: "fa-light fa-palette",
  weight: 6,
  menu: [{
    type: "swivucolorfield",
    cls: "swivucolorfield task-editor-v2-field",
    editable: false,
    clearable: false,
  }],
  ...params
})

export const entityCellTaskStyle = (params = {}) => ({
  text: "Task Style",
  icon: "fa-light fa-palette",
  weight: 7,
  menu: [{
    type: "combo",
    contentElementCls: "b-swivu-task-style",
    listCls: "board-task-style-list b-sch-horizontal",
    // listItemTpl: renderTaskSyleList,
    listItemTpl: (item) => capitalizeText(item.value),
    editable: false,
    clearable: false,
    field: "taskStyle",
  }],
  ...params
})

export const cellMenuItemMarkRead = (params = {}) => ({
  text: "Mark Selected as Read",
  icon: "fa-light fa-eye",
  ...params,
})

export const cellMenuItemMarkUnread = (params = {}) => ({
  text: "Mark Selected as Unread",
  icon: "fa-light fa-eye-slash",
  ...params,
})

export const cellMenuItemAddImage = (params = {}) => ({
  text: "Add Image",
  icon: "fa-light fa-image",
  ...params
})

export const cellMenuItemDeleteImage = (params = {}) => ({
  text: "Delete Image",
  icon: "fa-light fa-image-slash",
  ...params
})

export const cellMenuItemInsertAbove = (params = {}) => ({
  text: "Insert Above",
  icon: "fa-thin fa-circle-plus",
  ...params,
})

export const cellMenuItemInsertBelow = (params = {}) => ({
  text: "Insert Below",
  icon: "fa-thin fa-circle-plus",
  ...params,
})

export const cellMenuItemNudge = (params = {}) => ({
  text: "Nudge",
  icon: "fa-solid fa-bulldozer",
  ...params
})

export const cellMenuItemAdjustPoints = (params = {}) => ({
  text: "Adjust Points",
  icon: 'fa-regular fa-sliders-simple',
  ...params
})

const getPrefixFromStoreId = (id) => {
  switch (id) {
    case "clients":
      return "Client";
    case "resources":
      return "Resource";
    case "reminders":
      return "Alert";
    case "taskbudgets":
      return "Budget";
    case STORE_KEYS.incentivePointsStore:
      return "Point";
    default:
      return "Task";
  }
};

export const performOperationWithHandlingRefreshes = (grid, cb) => async (...args) => {
  grid.suspendRefresh();

  // Use store's bulk operation method if available for better performance with filtering
  if (grid.store.performBulkOperation) {
    grid.store.performBulkOperation(() => cb(...args));
  } else {
    cb(...args);
  }

  if (grid.store.changes) {
    const recordsToProcess = Object.values(grid.store.changes).flat();
    const MAX_RECORDS_TO_PROCESS = 40;
    const hasReachThreshold = recordsToProcess.length > MAX_RECORDS_TO_PROCESS;

    if (hasReachThreshold) {
      if (grid.store.project) {
        grid.mask(`We are processing updates to ${recordsToProcess.length} records`)
        await grid.store.project.sync();
        grid.unmask();
      } else {
        console.warn("Unable to optimize run batch updates as project instance is not available on the store");
      }
    }
  }

  grid.resumeRefresh();
}

export const handleOnRecordDelete = (records, store, canPrompt, cb = () => {}) => () => {
  cb();
  if (canPrompt) {
    const prefix = getPrefixFromStoreId(store.storeId);
    const isMultiple = records.length > 1;
    MessageDialog.confirm({
      okButton: "Delete",
      cancelButton: "Cancel",
      title: `Delete ${prefix}${isMultiple ? "s" : ""}`,
      message: `Are you sure you want to delete "${
        isMultiple ? `${records.length} ${prefix}s` : (records[0].name || records[0].entityVisibleName)
      }"?`
    }).then((response) => {
      if (response === MessageDialog.okButton) {
        store.remove(records);
      }
    })
  } else {
    store.remove(records);
  }
}


export const buildEntityTagSelectorMenu = ({ grid, record, selectedItems, items, project, swivuGroupColumn, isMultiSelect, tagStoreKey }) => {
  // Todo: When creating client status column, add it's type in below array as well
  const statusColumnTypes = [WIDGET_KEYS.columnTaskStatus, WIDGET_KEYS.columnResourceStatus, WIDGET_KEYS.columnContactStatus];
  const categoryColumnTypes = [WIDGET_KEYS.columnTaskCategory, WIDGET_KEYS.columnResourceCategory, WIDGET_KEYS.columnClientCategory, WIDGET_KEYS.columnContactCategory];
  const tagColumnTypes = [WIDGET_KEYS.columnTaskTag, WIDGET_KEYS.columnResourceTag, WIDGET_KEYS.columnClientTag, WIDGET_KEYS.columnContactTag]
  const getSelector = ({ tagStoreName, tagKey, property, record }) => {
    const tagStore = project.getCrudStore(tagStoreName);
    const tagStoreRecordsForKey = tagStore.query((tagRecord) => tagRecord.tagSet === tagKey, true);

    const isChecked = (tagRecord) => {
      if (isMultiSelect) return false;
      const assignPropertyName = `${property}Id`;
      const recordToCheck = selectedItems[0] || record;
      if (assignPropertyName === "tagsId") {
        return Array.isArray(recordToCheck[assignPropertyName]) && recordToCheck[assignPropertyName].includes(tagRecord.id);
      } else {
        return recordToCheck[assignPropertyName] === tagRecord.id;
      }
    }

    return tagStoreRecordsForKey.map((tagRecord) => {
      const assignPropertyName = `_${property}`;
      const isMultiTagProperty = assignPropertyName === "_tags";
      return ({
        text: tagRecord.tagValue,
        closeParent: !isMultiTagProperty,
        swivuTagRecord: tagRecord, // adding tag record so it can be accessed after
        checked: isChecked(tagRecord),
        cls: "flex flex-row-reverse gap-x-1.5 swivu-taskTagValue-menu-item",
        onItem(e) {
          performOperationWithHandlingRefreshes(grid, () => {
            let tagId = null;
            if (isMultiTagProperty) {
              tagId = e.item.parent.items.filter((menuItem) => menuItem.checked).map((checkedItems) => checkedItems.swivuTagRecord.id);
            } else {
              tagId = e.item.checked ? tagRecord.id: null;
            }

            selectedItems.forEach((selectedItem) => {
              selectedItem[assignPropertyName] = tagId;
            });
          })();
        }
      })
    });
  }

  if (items.entityPhaseSelector && (!swivuGroupColumn || swivuGroupColumn.type === WIDGET_KEYS.columnTaskPhase)) {
    items.entityPhaseSelector.menu = getSelector({ tagStoreName: tagStoreKey, tagKey: TAGS_KEYS.phase, property: "phase", record });
    items.entityPhaseSelector.disabled = !items.entityPhaseSelector.menu.length;
  }

  if (items.entityStatusSelector && (!swivuGroupColumn || statusColumnTypes.includes(swivuGroupColumn.type))) {
    items.entityStatusSelector.menu = getSelector({ tagStoreName: tagStoreKey, tagKey: TAGS_KEYS.status, property: "status", record });
    items.entityStatusSelector.disabled = !items.entityStatusSelector.menu.length;
  }

  if (items.entityCategorySelector && (!swivuGroupColumn || categoryColumnTypes.includes(swivuGroupColumn.type))) {
    items.entityCategorySelector.menu = getSelector({ tagStoreName: tagStoreKey, tagKey: TAGS_KEYS.category, property: "category", record });
    items.entityCategorySelector.disabled = !items.entityCategorySelector.menu.length;
  }

  if (items.entityTagsSelector && (!swivuGroupColumn || tagColumnTypes.includes(swivuGroupColumn.type))) {
    items.entityTagsSelector.menu = getSelector({ tagStoreName: tagStoreKey, tagKey: TAGS_KEYS.tag, property: "tags", record });
    items.entityTagsSelector.disabled = !items.entityTagsSelector.menu.length;
  }

  if (items.entityTeamSelector && (!swivuGroupColumn || swivuGroupColumn.type === WIDGET_KEYS.teamField)) {
    const tagStore = project.getCrudStore(STORE_KEYS.boardTeamsStore);
    const teamRecords = tagStore.query(() => true, true);
    const isChecked = (teamRecord) => (selectedItems[0] || record).teamId === teamRecord.id;
    items.entityTeamSelector.menu = teamRecords.map((teamRecord) => ({
      text: teamRecord.name,
      closeParent: true,
      checked: isChecked(teamRecord),
      cls: "flex flex-row-reverse gap-x-1.5 swivu-taskTagValue-menu-item",
      onItem(e) {
        performOperationWithHandlingRefreshes(grid, () => {
          selectedItems.forEach((selectedItem) => {
            selectedItem._team = e.item.checked ? teamRecord.id: null;
          });
        })();
      }
    }));
    items.entityTeamSelector.disabled = !items.entityTeamSelector.menu.length;
  }

}

// The function is being used by all main pages since all of them almost share the same logic, so why not unify it
export const processCellMenuItems = ({
  editorHandler,
  grid,
  user,
  onEditEntity,
  entityName = "record",
  promptOnRemove = false,
  project,
  tagStoreKey
}) => (e) => {
  const { items, record, selection  } = e;
  // we need to spread out the group children records as, when we try to archive the first record then the rest of the records never archived as the groupChildren array gets empty
  // spreading out into the new array will keep the original records in place
  let selectedItems = [...(record.groupChildren || selection)];
  // In schedule grid we have the ability to disable row selection/highlight from personal setting which brings an issue where open menu from row won't populate the selectedItems property
  // In that case we are adding record to the selectedItems
  if (!selectedItems.length) selectedItems = [record];
  const isMultiSelect = record.isGroupHeader || selection.length > 1;
  const countText = `${selectedItems.length} ${pluralize(selectedItems.length, entityName)}`;

  if (items.editEntity && onEditEntity) {
    if (isMultiSelect) items.editEntity.hidden = true;
    else items.editEntity.onItem = () => onEditEntity(record);
  }

  if (items.entitySendBoardAlert) {
    items.entitySendBoardAlert.onItem = () => {
      const newRecord = project.reminderStore.createRecord({ alertType: ALERT_TYPES.BoardAlert });
      let aggregateUsersFunction = null;
      if (record.isUser) {
        aggregateUsersFunction = (prevItems, boardUser) => {
          prevItems.push(boardUser);
          return prevItems;
        };
      } else {
        aggregateUsersFunction = (prevItems, record) => prevItems.concat(record.assignedBoardUsers);
      }
      // const defaultSelectedBoardUsers = selectedItems.map((selectedResource) => (selectedResource.assignedBoardUsers || [])).flat();
      const defaultSelectedBoardUsers = selectedItems.reduce(aggregateUsersFunction, []);
      editorHandler.openEditor(EDITOR_KEYS.boardAlertEditor, null, { reminderRecord: newRecord, defaultSelectedBoardUsers }, { by: "recordType", useExisting: true });
    }
  }

  if (record.isGroupHeader) {
    if (record.swivuGroupColumn) {
      buildEntityTagSelectorMenu({ grid, record, selectedItems, items, project, swivuGroupColumn: record.swivuGroupColumn, tagStoreKey});
    }
  } else {
    buildEntityTagSelectorMenu({ grid, record, selectedItems, items, project, isMultiSelect: selection.length > 1, tagStoreKey });
  }

  if (items.entityTaskColor) {
    if (!isMultiSelect) {
      buildTaskEntityColorMenu({ item: items.entityTaskColor, record, selectedItems });
    } else {
      items.entityTaskColor.hidden = true;
    }
  }

  if (items.entityCellTaskStyle) {
    if (!isMultiSelect) {
      buildTaskEntityStyleMenu({ item: items.entityCellTaskStyle, record, selectedItems });
    } else {
      items.entityCellTaskStyle.hidden = true;
    }
  }

  /* View Agenda Menu Item */
  if (items.viewAgenda) {
    if (isMultiSelect) items.viewAgenda.hidden = true;
    else items.viewAgenda.onItem = () => {
      openAgendaView();
    }
  }

  if (items.removeRow && promptOnRemove) {
    items.removeRow.onItem = handleOnRecordDelete((selection.length > 0 ? selection : [record]), grid.store, promptOnRemove);
  }

  /* Archive/UnArchive Menu Item */
  if (items.archive) { // if we have archive menu item
    if (isMultiSelect) {
      items.removeRow.hidden = true;

      // handle logic for display Archive and UnArchive button at the same time if records in group are semi archived
      const isEveryRecordArchive = selectedItems.every((e) => e.archived);
      if (!isEveryRecordArchive) {
        items.archive.text = `Archive ${countText}`;
        items.archive.onItem = () => {
          const swivuUnlinkPromptConfig = selectedItems.at(0).swivuUnlinkPromptConfig;
          if (swivuUnlinkPromptConfig) {
            showUnlinkRelatedEntityPrompt({
              ...swivuUnlinkPromptConfig,
              record: selectedItems,
              relatedRecords: selectedItems.map((record) => record.tasks).flat(),
            }).then((isArchive) => {
              if (isArchive) {
                grid.deselectAll();
              }
            })
          } else {
            performOperationWithHandlingRefreshes(grid, () => {
              selectedItems.forEach((item) => item.archived = true);
            })();
            grid.deselectAll();
          }
        }
      } else {
        items.archive.hidden = true;
      }
      const isEveryRecordUnArchive = selectedItems.every((e) => !e.archived);
      if (!isEveryRecordUnArchive) {
        items.unArchive = { icon: items.archive.icon, weight: items.archive.weight, text: `UnArchive ${countText}` };
        items.unArchive.onItem = () => {
          performOperationWithHandlingRefreshes(grid, () => {
            selectedItems.forEach((item) => item.archived = false);
          })();
        }
      }
      if (isEveryRecordArchive) {
        items.removeGroupRows = { icon: items.removeRow.icon, weight: items.archive.weight, text: `Delete ${countText}` };
        items.removeGroupRows.onItem = handleOnRecordDelete(selectedItems, grid.store, promptOnRemove);
      }
    } else {
      items.archive.text = record.archived ? "Unarchive" : "Archive";
      // Hide removeRow for non-archived items, except for checklists
      items.removeRow.hidden = !record.archived;
    }
  }

  if (items.nudgeUsers) {
    items.nudgeUsers.disabled = !(record.assignedBoardUsers?.length && user.can(record.canEdit))
    items.nudgeUsers.onItem = () => {
      triggerNudgeAlert(record.id, project, grid)
    }
  }

  if (items.importChecklistItem) {
    items.importChecklistItem.disabled = !user.can(record.canEdit);
  }

  if (items.adjustPoints) {
    items.adjustPoints.onItem = () => {
      openNewSpotIncentiveEditor({ project, editorHandler, otherParams: { selectedTask: record } });
    }
  }

  /* Collapse / Expand Row */
  if (record.isGroupHeader && items.collapseRow) {
    const isCollapsed = record.meta.collapsed; // internal property to identify group collapse state

    items.collapseRow.hidden = false;

    if (isCollapsed) {
      items.collapseRow.text = "Expand";
      items.collapseRow.icon = "fa-solid fa-chevron-down";
    } else {
      items.collapseRow.text = "Collapse";
      items.collapseRow.icon = "fa-solid fa-chevron-up";
    }

    items.collapseRow.onItem = () => {
      grid.features.group.toggleCollapse(record)
    }
  }

  /* Collapse / Expand All Rows */
  if (record.isGroupHeader && items.collapseAllRows) {
    const isAllCollapsed = grid.store.groupRecords.values.every((g) => g.meta.collapsed);

    items.collapseAllRows.hidden = false;

    if (isAllCollapsed) {
      items.collapseAllRows.text = "Expand All";
      items.collapseAllRows.icon = "fa-solid fa-chevrons-down";
    } else {
      items.collapseAllRows.text = "Collapse All";
      items.collapseAllRows.icon = "fa-solid fa-chevrons-up";
    }

    items.collapseAllRows.onItem = () => {
      if (isAllCollapsed) {
        grid.expandAll();
      } else {
        grid.collapseAll();
      }
    }
  }

  /* Settings Editor */
  if (record.isGroupHeader && items.settingsEditor) {
    const isTagColumn = record.swivuGroupColumn && record.swivuGroupColumn.swivuIsTagColumn;
    const tabName = isTagColumn ? record.swivuGroupColumn.editor?.tabName: null;

    items.settingsEditor.hidden = !tabName;

    items.settingsEditor.onItem = () => {
      e.source.onClickSwivuEditMessage({ tabName: tabName });
    }
  }

  // Filter string equal Menu Item
  if (items.filterStringEquals && isMultiSelect) {
    items.filterStringEquals.hidden = true;
  }

  // Checklist group header handling
  if (record.isGroupHeader && items.exportChecklist) {
    items.exportChecklist.hidden = true;
  }

  if (record.isGroupHeader && items.copyToClipboard) {
    items.copyToClipboard.hidden = true;
  }

  if (record.isGroupHeader && items.addImage) {
    items.addImage.hidden = true;
  }

  if (record.isGroupHeader && items.insertAbove) {
    items.insertAbove.hidden = true;
  }

  if (record.isGroupHeader && items.insertBelow) {
    items.insertBelow.hidden = true;
  }

  if (record.isGroupHeader && items.nudgeUsers) {
    items.nudgeUsers.hidden = true;
  }

  if (items.adjustPoints) {
    if (isMultiSelect) {
      items.adjustPoints.hidden = true;
    } else if (!checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS })) {
      items.adjustPoints.disabled = true;
    }
  }

  if (items.search) {
    items.search.hidden = true;
  }
};
